/**
 * MagicCreate.js - AI Creation Engine Frontend 🎬✨
 * 
 * Kopiert die Galerie-Logik von BeatleapWizard für identisches Verhalten.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API_URL } from '../../config';
import { apiClient } from '../../utils/apiClient';
import BeatleapStudio from '../beatleap/BeatleapStudio';

const STORAGE_KEY = 'magic_job_state';

const MagicCreate = ({ avatarId, onComplete, onBack, generations: propsGenerations, videos: propsVideos }) => {
  const [step, setStep] = useState('select'); // 'select' | 'config' | 'processing' | 'voting'
  
  // Gallery state (same as BeatleapWizard)
  const [galleryClips, setGalleryClips] = useState([]); // Leer - Mini-Galerie entfernt
  const [generationCount, setGenerationCount] = useState(0); // Anzahl Generationen (nicht einzelne Bilder)
  const [loadingClips, setLoadingClips] = useState(true);
  const [selectedClips, setSelectedClips] = useState([]);
  
  // Config state
  const [selectedMusic, setSelectedMusic] = useState('phonk');
  const [duration, setDuration] = useState(15);
  const [musicTracks, setMusicTracks] = useState([]);
  
  // Processing state
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [variants, setVariants] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  
  // Video refs for individual control
  const videoRefA = useRef(null);
  const videoRefB = useRef(null);
  
  // Beatleap Studio state
  const [showBeatleapStudio, setShowBeatleapStudio] = useState(false);
  const [editVideoUrl, setEditVideoUrl] = useState(null);
  const [editProjectId, setEditProjectId] = useState(null); // 💾 Projekt-ID zum Weiterbearbeiten
  
  // Project Preview state
  const [previewProject, setPreviewProject] = useState(null);
  const previewVideoRef = useRef(null);
  const [previewState, setPreviewState] = useState({
    playing: false,
    muted: false,
    currentTime: 0,
    duration: 0
  });
  
  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState(null); // project id to delete
  
  // Gallery tabs
  const [activeGalleryTab, setActiveGalleryTab] = useState('projekte');
  const [myProjects, setMyProjects] = useState([]);
  const [selectedProjectIds, setSelectedProjectIds] = useState(new Set()); // Multi-Select Projects
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false); // Bulk-Delete Confirm Projects
  const [selectedGalleryIds, setSelectedGalleryIds] = useState(new Set()); // Multi-Select Gallery
  const [showGalleryBulkDeleteConfirm, setShowGalleryBulkDeleteConfirm] = useState(false); // Bulk-Delete Confirm Gallery
  const [playingProjectId, setPlayingProjectId] = useState(null); // 🎬 Inline Video Preview
  const [loadingProjects, setLoadingProjects] = useState(false);
  
  // Per-video state: { a: { playing, muted, currentTime, duration }, b: { ... } }
  const [videoState, setVideoState] = useState({
    a: { playing: false, muted: true, currentTime: 0, duration: 0 },
    b: { playing: false, muted: true, currentTime: 0, duration: 0 }
  });
  
  // Format time as M:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Preview player controls
  const togglePreviewPlay = () => {
    const video = previewVideoRef.current;
    if (!video) return;
    
    if (previewState.playing) {
      video.pause();
    } else {
      video.play().catch(() => {});
    }
    setPreviewState(prev => ({ ...prev, playing: !prev.playing }));
  };
  
  const togglePreviewMute = () => {
    const video = previewVideoRef.current;
    if (!video) return;
    
    video.muted = !video.muted;
    setPreviewState(prev => ({ ...prev, muted: !prev.muted }));
  };
  
  const handlePreviewTimeUpdate = (e) => {
    setPreviewState(prev => ({
      ...prev,
      currentTime: e.target.currentTime,
      duration: e.target.duration || 0
    }));
  };
  
  const handlePreviewSeek = (e) => {
    const video = previewVideoRef.current;
    if (!video || !previewState.duration) return;
    
    const newTime = parseFloat(e.target.value);
    video.currentTime = newTime;
    setPreviewState(prev => ({ ...prev, currentTime: newTime }));
  };
  
  const openProjectPreview = (project) => {
    setPreviewProject(project);
    setPreviewState({ playing: false, muted: false, currentTime: 0, duration: 0 });
  };
  
  const closeProjectPreview = () => {
    const video = previewVideoRef.current;
    if (video) video.pause();
    setPreviewProject(null);
    setPreviewState({ playing: false, muted: false, currentTime: 0, duration: 0 });
  };
  
  const openStudioFromPreview = () => {
    if (!previewProject) return;
    const video = previewVideoRef.current;
    if (video) video.pause();
    setEditVideoUrl(previewProject.url);
    setPreviewProject(null);
    setShowBeatleapStudio(true);
  };
  
  // Delete project - show confirmation
  const deleteProject = (projectId, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setDeleteConfirm(projectId);
  };
  
  // Actually delete after confirmation
  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    
    const projectId = deleteConfirm;
    setDeleteConfirm(null);
    
    try {
      const response = await apiClient.delete(`/api/beatleap/project/${projectId}`);
      if (response.ok) {
        // Remove from local state
        setMyProjects(prev => prev.filter(p => p.id !== projectId));
        // Close preview if this project was being previewed
        if (previewProject?.id === projectId) {
          closeProjectPreview();
        }
      } else {
        console.error('Delete failed with status:', response.status);
      }
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };
  
  const cancelDelete = () => {
    setDeleteConfirm(null);
  };

  // Multi-Select Functions
  const toggleProjectSelection = (projectId, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setSelectedProjectIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });
  };

  const clearSelection = () => {
    setSelectedProjectIds(new Set());
  };

  const deleteSelectedProjects = () => {
    if (selectedProjectIds.size === 0) return;
    setShowBulkDeleteConfirm(true);
  };

  const confirmBulkDelete = async () => {
    setShowBulkDeleteConfirm(false);
    
    const idsToDelete = Array.from(selectedProjectIds);
    
    // Delete all selected projects
    for (const projectId of idsToDelete) {
      try {
        await apiClient.delete(`/api/beatleap/project/${projectId}`);
      } catch (err) {
        console.error(`Delete failed for ${projectId}:`, err);
      }
    }
    
    // Remove from local state
    setMyProjects(prev => prev.filter(p => !selectedProjectIds.has(p.id)));
    setSelectedProjectIds(new Set());
    
    // Close preview if deleted
    if (previewProject && selectedProjectIds.has(previewProject.id)) {
      closeProjectPreview();
    }
  };

  const cancelBulkDelete = () => {
    setShowBulkDeleteConfirm(false);
  };

  // Gallery Multi-Select Functions
  const toggleGallerySelection = (clipId, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setSelectedGalleryIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(clipId)) {
        newSet.delete(clipId);
      } else {
        newSet.add(clipId);
      }
      return newSet;
    });
  };

  const clearGallerySelection = () => {
    setSelectedGalleryIds(new Set());
  };

  const deleteSelectedGalleryItems = () => {
    if (selectedGalleryIds.size === 0) return;
    setShowGalleryBulkDeleteConfirm(true);
  };

  const confirmGalleryBulkDelete = async () => {
    setShowGalleryBulkDeleteConfirm(false);
    
    const idsToDelete = Array.from(selectedGalleryIds);
    
    for (const clipId of idsToDelete) {
      try {
        // clipId = "img_genId_index" → parse it
        const parts = clipId.replace('img_', '').split('_');
        const imageIndex = parseInt(parts.pop(), 10);
        const genId = parts.join('_');
        
        await apiClient.delete(
          `/api/generations/${genId}?image_index=${imageIndex}${avatarId ? `&avatar_id=${avatarId}` : ''}`
        );
      } catch (err) {
        console.error(`Delete failed for ${clipId}:`, err);
      }
    }
    
    // Remove from local states
    setGalleryClips(prev => prev.filter(c => !selectedGalleryIds.has(c.id)));
    setSelectedClips(prev => prev.filter(c => !selectedGalleryIds.has(c.id)));
    setSelectedGalleryIds(new Set());
  };

  const cancelGalleryBulkDelete = () => {
    setShowGalleryBulkDeleteConfirm(false);
  };
  
  // Handle video time update
  const handleTimeUpdate = (v, video) => {
    setVideoState(prev => ({
      ...prev,
      [v]: { 
        ...prev[v], 
        currentTime: video.currentTime,
        duration: video.duration || 0
      }
    }));
  };
  
  // 💾 Restore state from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const { variants: savedVariants, jobId: savedJobId } = JSON.parse(saved);
        if (savedVariants && (savedVariants.a?.video_url || savedVariants.b?.video_url)) {
          console.log('🎬 Restoring saved job state:', savedJobId);
          setVariants(savedVariants);
          setJobId(savedJobId);
          setStep('voting');
        }
      }
    } catch (e) {
      console.error('Failed to restore magic state:', e);
    }
  }, []);
  
  // 💾 Save state to localStorage when variants change
  useEffect(() => {
    if (variants && step === 'voting') {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ variants, jobId }));
        console.log('🎬 Saved job state to localStorage');
      } catch (e) {
        console.error('Failed to save magic state:', e);
      }
    }
  }, [variants, jobId, step]);
  
  // 🔒 Lock body scroll in ALL Magic screens + Prevent Pull-to-Refresh
  useEffect(() => {
    // Save scroll position
    const scrollY = window.scrollY;
    
    // Lock body scroll
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.touchAction = 'none';
    document.body.style.overscrollBehavior = 'none';
    
    // Track touch start position
    let touchStartY = 0;
    
    const handleTouchStart = (e) => {
      touchStartY = e.touches[0].clientY;
    };
    
    // Aggressively prevent pull-to-refresh
    const preventPullToRefresh = (e) => {
      if (!e.touches || e.touches.length !== 1) return;
      
      const touchY = e.touches[0].clientY;
      const scrollableEl = e.target.closest('[data-scrollable]');
      
      // If pulling down and at top of scroll container (or no scroll container)
      if (touchY > touchStartY) {
        if (!scrollableEl || scrollableEl.scrollTop <= 0) {
          e.preventDefault();
        }
      }
    };
    
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', preventPullToRefresh, { passive: false });
    
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      document.body.style.touchAction = '';
      document.body.style.overscrollBehavior = '';
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', preventPullToRefresh);
      // Restore scroll position
      window.scrollTo(0, scrollY);
    };
  }, []);
  
  // Clear saved state when user makes a choice or goes back
  const clearSavedState = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
  };

  // Load gallery - Bilder aus Props, Videos vom Server
  useEffect(() => {
    if (!avatarId) return;
    
    setLoadingClips(true);
    
    // 🔄 SYNC FIX: Lade BEIDES vom Server (nicht aus Props!)
    const imgUrl = `/api/generations/list?avatar_id=${avatarId}`;
    const vidUrl = `/api/videos/list?avatar_id=${avatarId}`;
    
    Promise.all([
      apiClient.get(imgUrl).then(r => r.json()).catch(() => ({ generations: [] })),
      apiClient.get(vidUrl).then(r => r.json()).catch(() => ({ videos: [] }))
    ]).then(([imgData, vidData]) => {
      const clips = [];
      let imageCount = 0;
      let videoCount = 0;
      
      // BILDER vom Server (immer aktuell!)
      (imgData.generations || []).forEach(gen => {
        const imageArray = gen.image_urls || gen.images || [];
        imageArray.forEach((img, idx) => {
          imageCount++;
          clips.push({
            id: `img_${gen.id || gen._id}_${idx}`,
            type: 'image',
            url: img,
            thumbnail: img,
            createdAt: gen.created_at || gen.createdAt
          });
        });
      });
      
      // VIDEOS vom Server
      (vidData.videos || []).forEach(vid => {
        if (vid.status === 'completed' && vid.video_url) {
          videoCount++;
          clips.push({
            id: `vid_${vid.id || vid._id}`,
            type: 'video',
            url: vid.video_url,
            thumbnail: vid.thumbnail_url || vid.source_image_url || vid.video_url,
            createdAt: vid.created_at || vid.createdAt,
            duration: vid.duration || 5
          });
        }
      });
      
      clips.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      setGenerationCount(imageCount + videoCount);
      setGalleryClips(clips);
      setLoadingClips(false);
      
      console.log('🎬 Magic: Bilder:', imageCount, '| Videos:', videoCount, '| Total:', clips.length);
    });
      
    // Load music
    fetch(`${API_URL}/api/magic/music`)
      .then(r => r.json())
      .then(data => setMusicTracks(data.tracks || []))
      .catch(console.error);
      
  }, [avatarId]); // Nur bei Avatar-Wechsel neu laden

  // Load my projects when tab switches (Draft-Projekte aus Beatleap Studio)
  useEffect(() => {
    if (activeGalleryTab === 'projekte') {
      setLoadingProjects(true);
      console.log('📂 Lade Projekte...');
      apiClient.get('/api/beatleap/projects')
        .then(response => response.json())  // ← WICHTIG: fetch Response → JSON
        .then(data => {
          console.log('📂 Projekte Response:', data);
          const projects = (data?.projects || []).map(p => {
            const config = p.project_config || {};
            const clips = config.clips || [];
            const totalDuration = clips.reduce((acc, c) => acc + (c.duration || 3), 0);
            const thumbnail = clips[0]?.thumbnail || clips[0]?.url || null;
            
            // Video-URL für Preview: videoUrl ODER erster Clip mit videoUrl
            const firstVideoClip = clips.find(c => c.videoUrl);
            const previewUrl = p.videoUrl || firstVideoClip?.videoUrl || null;
            
            return {
              id: p.id,
              type: 'draft',
              thumbnail: thumbnail,
              clipCount: p.clipCount || clips.length,
              duration: totalDuration,
              audioTracks: config.audioTracks?.length || 0,
              textTracks: config.textTracks?.length || 0,
              updatedAt: p.updated_at || p.created_at,
              createdAt: p.created_at,
              // Magic-Felder
              source: p.source,
              name: p.name,
              music_track: p.music_track,
              style_name: p.style_name,
              variant: p.variant,
              videoUrl: p.videoUrl,
              previewUrl: previewUrl,  // 🎬 Für Inline Video Preview
              project_config: config   // 🔧 Für Fallback
            };
          });
          setMyProjects(projects);
          
          // 🚀 PRELOAD: Projekt-Thumbnails im Hintergrund laden
          projects.slice(0, 20).forEach(p => {
            if (p.thumbnail) {
              const img = new Image();
              img.src = p.thumbnail;
            }
          });
        })
        .catch(err => {
          console.error('❌ Projekte laden fehlgeschlagen:', err);
          setMyProjects([]);
        })
        .finally(() => setLoadingProjects(false));
    }
  }, [activeGalleryTab]);

  const toggleClip = (clip) => {
    setSelectedClips(prev => {
      const exists = prev.find(c => c.id === clip.id);
      if (exists) {
        return prev.filter(c => c.id !== clip.id);
      } else {
        return [...prev, clip];
      }
    });
  };

  const startMagic = async () => {
    if (selectedClips.length < 1) {
      alert('Wähle mindestens 1 Bild oder Video aus');
      return;
    }
    
    setStep('processing');
    
    try {
      const assetUrls = selectedClips.map(c => c.url);
      
      console.log('🎬 Starting magic with', assetUrls.length, 'assets');
      
      const response = await fetch(`${API_URL}/api/magic/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          assets: assetUrls,
          music_track: selectedMusic,
          target_duration: duration,
          aspect_ratio: '9:16',
          avatar_id: avatarId,  // damit das Magic-Video dem aktiven Avatar zugeordnet wird
        })
      });
      
      if (!response.ok) throw new Error('Magic creation failed');
      
      const data = await response.json();
      setJobId(data.job_id);
      // 🏆 Use SSE for real-time updates!
      startSSEStream(data.job_id);
      
    } catch (err) {
      console.error('Magic failed:', err);
      alert('Fehler: ' + err.message);
      setStep('config');
    }
  };

  // 🏆 CHAMPIONS LEAGUE: Server-Sent Events for Real-Time Progress!
  const startSSEStream = useCallback((id) => {
    console.log('🏆 Starting SSE stream for job:', id);
    
    const eventSource = new EventSource(`${API_URL}/api/magic/stream/${id}`);
    
    eventSource.onmessage = (event) => {
      try {
        const status = JSON.parse(event.data);
        
        console.log('🏆 SSE Update:', status.status, status.progress + '%', status.message);
        setJobStatus(status);
        
        if (status.error) {
          console.error('🏆 SSE Error:', status.error);
          eventSource.close();
          alert('Fehler: ' + status.error);
          setStep('config');
          return;
        }
        
        if (status.status === 'completed') {
          console.log('🏆 Job completed! Variants:', status.variants);
          eventSource.close();
          setVariants(status.variants);
          setStep('voting');
        } else if (status.status === 'failed') {
          console.error('🏆 Job failed:', status.error);
          eventSource.close();
          alert('Generierung fehlgeschlagen: ' + (status.error || status.message || 'Unbekannter Fehler'));
          setStep('config');
        }
      } catch (err) {
        console.error('🏆 SSE parse error:', err);
      }
    };
    
    eventSource.onerror = (err) => {
      console.error('🏆 SSE connection error:', err);
      eventSource.close();
      // Fallback to polling if SSE fails
      console.log('🏆 Falling back to polling...');
      pollStatusFallback(id);
    };
    
    // Store reference for cleanup
    return eventSource;
  }, []);
  
  // Fallback polling if SSE doesn't work
  const pollStatusFallback = useCallback(async (id) => {
    console.log('🎬 Fallback polling for job:', id);
    try {
      const res = await fetch(`${API_URL}/api/magic/status/${id}?_t=${Date.now()}`);
      if (!res.ok) throw new Error(`Status ${res.status}`);
      
      const status = await res.json();
      setJobStatus(status);
      
      if (status.status === 'completed') {
        setVariants(status.variants);
        setStep('voting');
      } else if (status.status === 'failed') {
        alert('Generierung fehlgeschlagen: ' + (status.error || status.message));
        setStep('config');
      } else {
        setTimeout(() => pollStatusFallback(id), 1500);
      }
    } catch (err) {
      console.error('Polling error:', err);
      setTimeout(() => pollStatusFallback(id), 2000);
    }
  }, []);

  const continueWithVariant = () => {
    if (!selectedVariant || !variants) return;
    onComplete?.({
      variant: selectedVariant,
      videoUrl: variants[selectedVariant]?.video_url,
      sequence: variants[selectedVariant]?.sequence
    });
  };

  // ═══════════════════════════════════════════════════════════════
  // GALERIE SELECTION SCREEN → DIREKT ZUR WERKBANK!
  // ═══════════════════════════════════════════════════════════════
  if (step === 'select') {
    return (
      <div style={styles.fullScreen}>
        {/* Header */}
        <div style={styles.header}>
          <button onClick={onBack} style={styles.closeBtn}>✕</button>
          <h2 style={styles.headerTitle}>🎬 SNAPCUT</h2>
          {/* Zur Werkstatt Button - wie im alten Design */}
          <button 
            onClick={() => {
              if (selectedClips.length >= 1) {
                setEditProjectId(null);
                setShowBeatleapStudio(true);
              }
            }}
            style={{
              padding: '10px 20px',
              borderRadius: '25px',
              border: 'none',
              background: selectedClips.length >= 1 ? '#10B981' : '#333',
              color: 'white',
              fontWeight: '600',
              fontSize: '15px',
              opacity: selectedClips.length >= 1 ? 1 : 0.5,
              cursor: selectedClips.length >= 1 ? 'pointer' : 'default'
            }}
          >
            Zur Werkstatt ›
          </button>
        </div>
        
        {/* Tabs - Galerie | MEINE PROJEKTE | STUDIO */}
        <div style={styles.tabBar}>
          {/* Galerie Tab ENTFERNT - nur noch Hauptgalerie */}
          <div 
            style={{
              ...(activeGalleryTab === 'projekte' ? styles.tabActive : styles.tabInactive),
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onClick={() => setActiveGalleryTab('projekte')}
          >
            PROJEKTE
            {myProjects.length > 0 && (
              <span style={{
                background: activeGalleryTab === 'projekte' ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.1)',
                color: activeGalleryTab === 'projekte' ? '#10B981' : '#888',
                fontSize: '11px',
                fontWeight: 'bold',
                padding: '2px 6px',
                borderRadius: '10px',
                minWidth: '20px',
                textAlign: 'center'
              }}>
                {myProjects.length}
              </span>
            )}
          </div>
          <div 
            style={styles.tabInactive}
            onClick={() => {
              // Direkt zur leeren Werkstatt
              setSelectedClips([]);
              setShowBeatleapStudio(true);
            }}
          >
            STUDIO
          </div>
        </div>
        
        {/* Hint entfernt - Anzahl ist im Button */}
        
        {/* Projekte Grid - Galerie entfernt */}
        <div style={styles.galleryContainer} data-scrollable="true">
          {(
            loadingProjects ? (
              <div style={styles.loading}>Lade Projekte...</div>
            ) : myProjects.length === 0 ? (
              <div style={styles.empty}>
                Noch keine Projekte.<br/>
                Erstelle dein erstes Magic Video!
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', padding: '0 8px' }}>
                {myProjects.map((project) => {
                  // Formatiere Datum + Uhrzeit
                  const formatDateTime = (dateStr) => {
                    if (!dateStr) return '';
                    const date = new Date(dateStr);
                    return date.toLocaleDateString('de-DE', { 
                      day: '2-digit', 
                      month: '2-digit',
                      year: 'numeric'
                    }) + ', ' + date.toLocaleTimeString('de-DE', {
                      hour: '2-digit',
                      minute: '2-digit'
                    });
                  };
                  
                  // Formatiere Dauer als MM:SS
                  const formatDuration = (seconds) => {
                    const mins = Math.floor(seconds / 60);
                    const secs = Math.round(seconds % 60);
                    return `${mins}:${secs.toString().padStart(2, '0')}`;
                  };
                  
                  return (
                  <div
                    key={project.id}
                    style={{
                      background: '#1a1a1a',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      border: project.source === 'magic' || project.name?.includes('Magic') 
                        ? '2px solid #10B981' 
                        : '1px solid rgba(255,255,255,0.1)',
                      boxShadow: project.source === 'magic' || project.name?.includes('Magic')
                        ? '0 0 12px rgba(16, 185, 129, 0.3)'
                        : 'none'
                    }}
                  >
                    {/* Thumbnail mit Inline Video Preview */}
                    {(() => {
                      // Video-URL: NUR echte Videos (kein Fallback auf Bilder!)
                      const previewUrl = project.previewUrl || project.videoUrl || null;
                      const posterUrl = project.thumbnail && !project.thumbnail.endsWith('.mp4') ? project.thumbnail : undefined;
                      
                      return (
                    <div 
                      style={{ position: 'relative', aspectRatio: '9/16', background: '#0a0a0a', cursor: previewUrl ? 'pointer' : 'default' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (previewUrl) {
                          if (playingProjectId === project.id) {
                            // Pause
                            const vid = document.getElementById(`preview-${project.id}`);
                            if (vid) { vid.pause(); }
                            setPlayingProjectId(null);
                          } else {
                            // Play
                            setPlayingProjectId(project.id);
                            setTimeout(() => {
                              const vid = document.getElementById(`preview-${project.id}`);
                              if (vid) { vid.play().catch(() => {}); }
                            }, 100);
                          }
                        }
                      }}
                    >
                      {/* Video oder Thumbnail */}
                      {previewUrl ? (
                        <video
                          id={`preview-${project.id}`}
                          src={previewUrl}
                          poster={posterUrl}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          playsInline
                          muted
                          loop
                          preload="auto"
                          ref={(el) => {
                            if (el && playingProjectId === project.id) {
                              el.play().catch(() => {});
                            } else if (el && playingProjectId !== project.id) {
                              el.pause();
                              el.currentTime = 0;
                            }
                          }}
                        />
                      ) : posterUrl ? (
                        <img 
                          src={project.thumbnail} 
                          alt="" 
                          style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.7 }}
                          loading="eager"
                          decoding="async"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#444' }}>
                          🎬
                        </div>
                      )}
                      {/* Play/Pause Badge - nur wenn Video vorhanden */}
                      {previewUrl && (
                        <div style={{
                          position: 'absolute',
                          bottom: 8,
                          right: 8,
                          background: playingProjectId === project.id ? 'rgba(239,68,68,0.9)' : 'rgba(0,0,0,0.8)',
                          color: 'white',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          padding: '3px 8px',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          {playingProjectId === project.id ? '⏸' : '▶'} {formatDuration(project.duration || 0)}
                        </div>
                      )}
                      {/* Clip Count Badge */}
                      <div style={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        background: '#10B981',
                        color: 'white',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        padding: '3px 8px',
                        borderRadius: '6px'
                      }}>
                        {project.clipCount || 0} Clips
                      </div>
                      {/* Selection Checkbox */}
                      <div 
                        onClick={(e) => toggleProjectSelection(project.id, e)}
                        style={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          width: 26,
                          height: 26,
                          borderRadius: '50%',
                          background: selectedProjectIds.has(project.id) ? '#EF4444' : 'rgba(0,0,0,0.6)',
                          border: selectedProjectIds.has(project.id) ? '2px solid #EF4444' : '2px solid rgba(255,255,255,0.5)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          zIndex: 10
                        }}
                      >
                        {selectedProjectIds.has(project.id) && (
                          <span style={{ color: 'white', fontSize: '14px', fontWeight: 'bold' }}>✓</span>
                        )}
                      </div>
                    </div>
                      );
                    })()}
                    
                    {/* Info */}
                    <div style={{ padding: '12px' }}>
                      <div style={{ color: '#888', fontSize: '11px', marginBottom: '8px' }}>
                        {formatDateTime(project.updatedAt)}
                      </div>
                      {/* Weiter bearbeiten Button */}
                      <button
                        onClick={() => {
                          // Projekt in BeatleapStudio öffnen
                          setEditProjectId(project.id);
                          setShowBeatleapStudio(true);
                        }}
                        style={{
                          width: '100%',
                          padding: '8px',
                          background: 'transparent',
                          color: '#10B981',
                          border: '1px solid rgba(16,185,129,0.4)',
                          borderRadius: '8px',
                          fontSize: '12px',
                          fontWeight: '500',
                          cursor: 'pointer'
                        }}
                      >
                        Bearbeiten
                      </button>
                    </div>
                  </div>
                );})}
              </div>
            )
          )}
        </div>
        
        {/* Bottom Bar - Projects/Gallery Delete Selection or Clips Selection */}
        {selectedProjectIds.size > 0 ? (
          <div style={{
            ...styles.bottomBar,
            background: '#0a0a0a',
            borderTop: '1px solid rgba(16,185,129,0.2)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '14px 20px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ color: '#10B981', fontWeight: '600', fontSize: '15px' }}>{selectedProjectIds.size}</span>
              <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>ausgewählt</span>
              <button 
                onClick={clearSelection}
                style={{ 
                  background: 'transparent', 
                  border: 'none', 
                  color: '#10B981', 
                  fontSize: '14px',
                  cursor: 'pointer',
                  marginLeft: '4px'
                }}
              >
                Abbrechen
              </button>
            </div>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                deleteSelectedProjects();
              }}
              style={{
                background: 'rgba(239,68,68,0.15)',
                color: '#EF4444',
                border: '1px solid rgba(239,68,68,0.3)',
                borderRadius: '8px',
                padding: '8px 16px',
                fontWeight: '500',
                fontSize: '14px',
                cursor: 'pointer',
                pointerEvents: 'auto'
              }}
            >
              Löschen
            </button>
          </div>
        ) : selectedGalleryIds.size > 0 ? (
          <div style={{
            ...styles.bottomBar,
            background: '#0a0a0a',
            borderTop: '1px solid rgba(16,185,129,0.2)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '14px 20px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ color: '#10B981', fontWeight: '600', fontSize: '15px' }}>{selectedGalleryIds.size}</span>
              <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>ausgewählt</span>
              <button 
                onClick={clearGallerySelection}
                style={{ 
                  background: 'transparent', 
                  border: 'none', 
                  color: '#10B981', 
                  fontSize: '14px',
                  cursor: 'pointer',
                  marginLeft: '4px'
                }}
              >
                Abbrechen
              </button>
            </div>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                deleteSelectedGalleryItems();
              }}
              style={{
                background: 'rgba(239,68,68,0.15)',
                color: '#EF4444',
                border: '1px solid rgba(239,68,68,0.3)',
                borderRadius: '8px',
                padding: '8px 16px',
                fontWeight: '500',
                fontSize: '14px',
                cursor: 'pointer',
                pointerEvents: 'auto'
              }}
            >
              Löschen
            </button>
          </div>
        ) : (
          <div style={styles.bottomBar}>
            <span style={{ color: '#10B981' }}>{selectedClips.length}</span>
            <span style={{ color: '#888', marginLeft: '8px' }}>ausgewählt</span>
          </div>
        )}
        
        {/* Project Preview Modal - MINIMALISTISCH & EDEL */}
        {previewProject && (
          <div style={styles.previewModal}>
            {/* Minimaler Header */}
            <div style={styles.previewHeaderMinimal}>
              <button onClick={closeProjectPreview} style={styles.previewBackBtnMinimal}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5M12 19l-7-7 7-7"/>
                </svg>
              </button>
              <span style={styles.previewTitleMinimal}>
                {previewProject.duration ? `${Math.round(previewProject.duration)}s` : ''} 
                {previewProject.music_track ? ` • ${previewProject.music_track}` : ''}
              </span>
              <button onClick={openStudioFromPreview} style={styles.previewEditBtnMinimal}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
              </button>
            </div>
            
            {/* VIDEO IM FOKUS */}
            <div style={styles.previewVideoContainerMinimal} onClick={togglePreviewPlay}>
              <video
                ref={previewVideoRef}
                src={previewProject.url}
                style={styles.previewVideoMinimal}
                playsInline
                webkit-playsinline="true"
                loop
                autoPlay
                muted={previewState.muted}
                onTimeUpdate={handlePreviewTimeUpdate}
                onLoadedMetadata={(e) => {
                  handlePreviewTimeUpdate(e);
                  e.target.play().catch(() => {});
                }}
                onPlay={() => setPreviewState(prev => ({ ...prev, playing: true }))}
                onPause={() => setPreviewState(prev => ({ ...prev, playing: false }))}
              />
              
              {/* Eleganter Play Overlay */}
              {!previewState.playing && (
                <div style={styles.previewPlayOverlayMinimal}>
                  <div style={styles.previewPlayBtnMinimal}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </div>
                </div>
              )}
            </div>
            
            {/* Professioneller Progress Bar */}
            <div style={styles.previewProgressMinimal}>
              {/* Custom Slider Track */}
              <div 
                style={styles.sliderTrack}
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const percent = (e.clientX - rect.left) / rect.width;
                  const newTime = percent * (previewState.duration || 0);
                  if (previewVideoRef.current) {
                    previewVideoRef.current.currentTime = newTime;
                  }
                }}
                onTouchStart={(e) => {
                  e.stopPropagation();
                  const touch = e.touches[0];
                  const rect = e.currentTarget.getBoundingClientRect();
                  const percent = Math.max(0, Math.min(1, (touch.clientX - rect.left) / rect.width));
                  const newTime = percent * (previewState.duration || 0);
                  if (previewVideoRef.current) {
                    previewVideoRef.current.currentTime = newTime;
                  }
                }}
                onTouchMove={(e) => {
                  e.stopPropagation();
                  const touch = e.touches[0];
                  const rect = e.currentTarget.getBoundingClientRect();
                  const percent = Math.max(0, Math.min(1, (touch.clientX - rect.left) / rect.width));
                  const newTime = percent * (previewState.duration || 0);
                  if (previewVideoRef.current) {
                    previewVideoRef.current.currentTime = newTime;
                  }
                }}
              >
                {/* Background Track */}
                <div style={styles.sliderBg} />
                {/* Progress Fill */}
                <div 
                  style={{
                    ...styles.sliderFill,
                    width: `${(previewState.currentTime / (previewState.duration || 1)) * 100}%`
                  }}
                />
                {/* Thumb */}
                <div 
                  style={{
                    ...styles.sliderThumb,
                    left: `${(previewState.currentTime / (previewState.duration || 1)) * 100}%`
                  }}
                />
              </div>
              <div style={styles.previewTimeRowMinimal}>
                <span>{formatTime(previewState.currentTime)}</span>
                <div style={styles.previewControlsMinimal}>
                  <button onClick={togglePreviewPlay} style={styles.previewIconBtn}>
                    {previewState.playing ? '⏸' : '▶'}
                  </button>
                  <button onClick={togglePreviewMute} style={styles.previewIconBtn}>
                    {previewState.muted ? '🔇' : '🔊'}
                  </button>
                </div>
                <span>{formatTime(previewState.duration)}</span>
              </div>
            </div>
            
            {/* Floating Action Buttons */}
            <div style={styles.previewActionsMinimal}>
              <button onClick={openStudioFromPreview} style={styles.previewMainBtn}>
                Bearbeiten
              </button>
              <a href={previewProject.url} download style={styles.previewSecBtn}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
              </a>
              <button onClick={() => deleteProject(previewProject.id)} style={styles.previewDelBtn}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                </svg>
              </button>
            </div>
          </div>
        )}
        
        {/* Beatleap Studio - WERKBANK mit ausgewählten Clips */}
        {showBeatleapStudio && (
          <BeatleapStudio
            isOpen={showBeatleapStudio}
            projectId={editProjectId} // 💾 Existierendes Projekt laden
            avatarId={avatarId} // FIX: avatarId für Magic-Button in Werkstatt
            resultVideo={editVideoUrl ? { url: editVideoUrl } : null}
            initialClips={editProjectId ? [] : selectedClips} // Bei Projekt-Load keine initialClips
            selectedTrack={{ id: selectedMusic, name: selectedMusic }}
            galleryClips={galleryClips}
            onClose={() => {
              setShowBeatleapStudio(false);
              setEditVideoUrl(null);
              setEditProjectId(null);
            }}
            onSaveToProjects={() => {
              setShowBeatleapStudio(false);
              setEditVideoUrl(null);
              setEditProjectId(null);
              setActiveGalleryTab('projekte'); // 💾 Direkt zu "Meine Projekte"
              setMyProjects([]); // Force reload
            }}
            onNewProject={() => {
              setShowBeatleapStudio(false);
              setEditVideoUrl(null);
              setEditProjectId(null);
              setSelectedClips([]);
            }}
          />
        )}
        
        {/* Delete Confirmation Dialog */}
        {deleteConfirm && (
          <div style={styles.deleteOverlay} onClick={cancelDelete}>
            <div style={styles.deleteDialog} onClick={(e) => e.stopPropagation()}>
              <h3 style={styles.deleteTitle}>Projekt unwiderruflich löschen?</h3>
              <p style={styles.deleteText}>Alle Daten werden gemäß DSGVO vollständig entfernt.</p>
              <div style={styles.deleteButtons}>
                <button onClick={cancelDelete} style={styles.deleteCancelBtn}>
                  Abbrechen
                </button>
                <button onClick={confirmDelete} style={styles.deleteConfirmBtn}>
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Delete Confirmation Dialog - Projects */}
        {showBulkDeleteConfirm && (
          <div style={styles.deleteOverlay} onClick={cancelBulkDelete}>
            <div style={styles.deleteDialog} onClick={(e) => e.stopPropagation()}>
              <h3 style={styles.deleteTitle}>{selectedProjectIds.size === 1 ? 'Projekt' : `${selectedProjectIds.size} Projekte`} unwiderruflich löschen?</h3>
              <p style={styles.deleteText}>Alle Daten werden gemäß DSGVO vollständig entfernt.</p>
              <div style={styles.deleteButtons}>
                <button onClick={cancelBulkDelete} style={styles.deleteCancelBtn}>
                  Abbrechen
                </button>
                <button onClick={confirmBulkDelete} style={styles.deleteConfirmBtn}>
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Delete Confirmation Dialog - Gallery */}
        {showGalleryBulkDeleteConfirm && (
          <div style={styles.deleteOverlay} onClick={cancelGalleryBulkDelete}>
            <div style={styles.deleteDialog} onClick={(e) => e.stopPropagation()}>
              <h3 style={styles.deleteTitle}>{selectedGalleryIds.size === 1 ? 'Bild' : `${selectedGalleryIds.size} Bilder`} unwiderruflich löschen?</h3>
              <p style={styles.deleteText}>Alle Daten werden gemäß DSGVO vollständig entfernt.</p>
              <div style={styles.deleteButtons}>
                <button onClick={cancelGalleryBulkDelete} style={styles.deleteCancelBtn}>
                  Abbrechen
                </button>
                <button onClick={confirmGalleryBulkDelete} style={styles.deleteConfirmBtn}>
                  OK
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // CONFIG SCREEN (Music + Duration)
  // ═══════════════════════════════════════════════════════════════
  if (step === 'config') {
    return (
      <div style={styles.container} data-scrollable="true">
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <h1 style={styles.title}>🎬 SNAPCUT</h1>
          <p style={{ color: '#888' }}>{selectedClips.length} Clips ausgewählt</p>
        </div>
        
        {/* Selected Preview */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>📸 Ausgewählte Clips</div>
          <div style={styles.previewRow}>
            {selectedClips.slice(0, 6).map((clip, i) => (
              <img key={i} src={clip.thumbnail} alt="" style={styles.previewThumb} loading="eager" decoding="async" />
            ))}
            {selectedClips.length > 6 && (
              <div style={styles.moreCount}>+{selectedClips.length - 6}</div>
            )}
          </div>
          <button onClick={() => setStep('select')} style={styles.changeBtn}>
            Clips ändern
          </button>
        </div>
        
        {/* Music */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>🎵 Musik</div>
          <div style={styles.musicGrid}>
            {musicTracks.filter(t => t.available).map(track => (
              <div
                key={track.id}
                style={{
                  ...styles.musicItem,
                  ...(selectedMusic === track.id ? styles.musicItemSelected : {})
                }}
                onClick={() => setSelectedMusic(track.id)}
              >
                <div style={{ fontWeight: '600', fontSize: '14px' }}>{track.name}</div>
                <div style={{ fontSize: '12px', color: '#888' }}>{track.bpm} BPM</div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Duration */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>⏱️ Clip-Länge</div>
          <div style={styles.durationValue}>{duration} Sekunden</div>
          <input
            type="range"
            min="5"
            max="60"
            value={duration}
            onChange={(e) => setDuration(parseInt(e.target.value))}
            style={{ width: '100%', accentColor: '#10B981' }}
          />
        </div>
        
        {/* Start Button */}
        <button style={styles.magicButton} onClick={startMagic}>
          ✨ VIDEO ERSTELLEN ✨
        </button>
        
        <button style={styles.backBtn} onClick={() => setStep('select')}>← Clips ändern</button>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // PROCESSING SCREEN
  // ═══════════════════════════════════════════════════════════════
  
  // Rotierende Wartesprüche
  const waitingMessages = [
    "☕ Zeit für einen Kaffee – dein Video wird gerade geschnitten",
    "✨ Unsere KI zaubert an deinem Meisterwerk",
    "🚀 Fast fertig – nur noch ein bisschen Geduld",
    "💫 Die Magie braucht einen Moment...",
    "🍿 Schnapp dir Popcorn – gleich geht's los",
    "🎨 Letzte Pinselstriche werden gesetzt",
    "⚡ KI-Power arbeitet auf Hochtouren",
    "🎭 Dein Video nimmt Form an..."
  ];
  
  const [waitingMsgIndex, setWaitingMsgIndex] = useState(0);
  
  // Rotiere Nachricht alle 15 Sekunden
  useEffect(() => {
    if (step !== 'processing') return;
    const interval = setInterval(() => {
      setWaitingMsgIndex(prev => (prev + 1) % waitingMessages.length);
    }, 15000);
    return () => clearInterval(interval);
  }, [step]);
  
  if (step === 'processing') {
    return (
      <div style={styles.container} data-scrollable="true">
        <div style={styles.processingBox}>
          <div style={styles.spinner}></div>
          <h2>✨ Magie wird gewirkt...</h2>
          <p style={{ color: '#888' }}>{jobStatus?.message || 'Zwei Videos werden erstellt'}</p>
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: `${jobStatus?.progress || 0}%` }} />
          </div>
          <p style={{ color: '#666', fontSize: '14px' }}>{jobStatus?.progress || 0}%</p>
          {/* Rotierender Wartespruch */}
          <p style={{ 
            color: '#10B981', 
            fontSize: '13px', 
            marginTop: '24px',
            opacity: 0.8,
            transition: 'opacity 0.5s'
          }}>
            {waitingMessages[waitingMsgIndex]}
          </p>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // VOTING SCREEN
  // ═══════════════════════════════════════════════════════════════
  
  // Toggle play/pause for a specific video
  const togglePlay = (v) => {
    const videoRef = v === 'a' ? videoRefA : videoRefB;
    const video = videoRef.current;
    if (!video) return;
    
    if (videoState[v].playing) {
      video.pause();
    } else {
      video.play().catch(() => {});
    }
    
    setVideoState(prev => ({
      ...prev,
      [v]: { ...prev[v], playing: !prev[v].playing }
    }));
  };
  
  // Toggle mute for a specific video
  const toggleMute = (v) => {
    const videoRef = v === 'a' ? videoRefA : videoRefB;
    const video = videoRef.current;
    if (!video) return;
    
    video.muted = !video.muted;
    setVideoState(prev => ({
      ...prev,
      [v]: { ...prev[v], muted: !prev[v].muted }
    }));
  };
  
  if (step === 'voting' && variants) {
    // Build full video URLs
    const getVideoUrl = (v) => {
      const url = variants[v]?.video_url;
      if (!url) return null;
      if (url.startsWith('http')) return url;
      return `https://snapshotpro.ai${url}`;
    };

    return (
      <div style={styles.container} data-scrollable="true">
        <h2 style={{ textAlign: 'center', marginBottom: '10px' }}>✨ Beide Videos gespeichert!</h2>
        <p style={{ textAlign: 'center', marginBottom: '20px', color: '#10B981', fontSize: '14px' }}>
          Wähle eines zum Bearbeiten - beide sind in "Meine Projekte"
        </p>
        
        <div style={styles.variantsGrid}>
          {['a', 'b'].map(v => {
            const videoUrl = getVideoUrl(v);
            const hasVideo = !!videoUrl;
            const videoRef = v === 'a' ? videoRefA : videoRefB;
            const state = videoState[v];
            const variantData = variants[v] || {};
            
            // Format timestamp
            const createdAt = variantData.created_at ? new Date(variantData.created_at) : new Date();
            const dateStr = createdAt.toLocaleDateString('de-DE');
            const timeStr = createdAt.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
            
            return (
              <div 
                key={v}
                style={{
                  ...styles.variantCard,
                  ...(selectedVariant === v ? styles.variantCardSelected : {}),
                  opacity: hasVideo ? 1 : 0.5
                }}
                onClick={() => hasVideo && setSelectedVariant(v)}
              >
                {/* Clean Video - No Overlay */}
                <div style={styles.videoContainer}>
                  {hasVideo ? (
                    <>
                      <video 
                        ref={videoRef}
                        style={styles.variantVideo}
                        src={videoUrl}
                        loop 
                        muted
                        playsInline
                        webkit-playsinline="true"
                        preload="auto"
                        onTimeUpdate={(e) => handleTimeUpdate(v, e.target)}
                        onLoadedMetadata={(e) => handleTimeUpdate(v, e.target)}
                        onError={(e) => console.error('Video error:', v, e)}
                      />
                      {/* Dezente Zeitanzeige unten rechts */}
                      <div style={styles.timeDisplay}>
                        {formatTime(state.currentTime)} / {formatTime(state.duration)}
                      </div>
                    </>
                  ) : (
                    <div style={styles.noVideo}>
                      <span style={{ fontSize: '40px' }}>⚠️</span>
                      <span>Nicht verfügbar</span>
                    </div>
                  )}
                </div>
                
                {/* Controls unter dem Video */}
                {hasVideo && (
                  <div style={styles.controlsRow}>
                    <button 
                      onClick={(e) => { e.stopPropagation(); togglePlay(v); }}
                      style={{
                        ...styles.controlBtnBelow,
                        background: state.playing ? 'rgba(16,185,129,0.3)' : 'rgba(255,255,255,0.1)'
                      }}
                    >
                      {state.playing ? '⏸' : '▶️'}
                    </button>
                    <button 
                      onClick={(e) => { e.stopPropagation(); toggleMute(v); }}
                      style={{
                        ...styles.controlBtnBelow,
                        background: !state.muted ? 'rgba(16,185,129,0.3)' : 'rgba(255,255,255,0.1)'
                      }}
                    >
                      {state.muted ? '🔇' : '🔊'}
                    </button>
                  </div>
                )}
                
                {/* Info + Metadaten */}
                <div 
                  style={{
                    ...styles.variantInfo,
                    background: selectedVariant === v ? 'rgba(16,185,129,0.2)' : 'transparent'
                  }}
                >
                  <div style={{ fontWeight: 'bold', fontSize: '14px' }}>
                    {selectedVariant === v ? '✓ ' : ''}VARIANTE {v.toUpperCase()}
                  </div>
                  <div style={{ fontSize: '12px', color: '#10B981', marginBottom: '6px' }}>
                    {variantData.style_name || (v === 'a' ? 'Dynamic & Fast' : 'Cinematic & Flow')}
                  </div>
                  
                  {/* Metadaten */}
                  <div style={styles.metaRow}>
                    <span>⏱ {state.duration > 0 ? Math.round(state.duration) : duration}s</span>
                    <span>📅 {dateStr}</span>
                    <span>🕐 {timeStr}</span>
                  </div>
                  <div style={styles.metaRow}>
                    <span>🎵 {selectedMusic}</span>
                    <span>📸 {selectedClips.length} Clips</span>
                  </div>
                  
                  {/* Download Button */}
                  {hasVideo && (
                    <a 
                      href={videoUrl} 
                      download={`magic_${v}.mp4`}
                      onClick={(e) => e.stopPropagation()}
                      style={styles.downloadBtn}
                    >
                      ⬇️ Download
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        <button
          style={{ ...styles.magicButton, opacity: selectedVariant ? 1 : 0.5 }}
          onClick={() => { 
            if (selectedVariant && variants[selectedVariant]) {
              const url = variants[selectedVariant].video_url;
              const fullUrl = url && url.startsWith('http') ? url : 'https://snapshotpro.ai' + url;
              setEditVideoUrl(fullUrl);
              setShowBeatleapStudio(true);
            }
          }}
          disabled={!selectedVariant}
        >
          {selectedVariant ? `Variante ${selectedVariant.toUpperCase()} bearbeiten` : 'Wähle eine Variante'}
        </button>
        
        <button style={styles.backBtn} onClick={() => { clearSavedState(); setVariants(null); startMagic(); }}>
          🔄 Neu generieren
        </button>
        <button style={styles.backBtn} onClick={() => { clearSavedState(); onBack(); }}>← Zurück</button>
        
        {showBeatleapStudio && (
          <BeatleapStudio
            isOpen={showBeatleapStudio}
            resultVideo={editVideoUrl}
            selectedTrack={{ id: selectedMusic, name: selectedMusic }}
            galleryClips={galleryClips}
            onClose={() => {
              setShowBeatleapStudio(false);
              setEditVideoUrl(null);
            }}
            onSaveToProjects={() => {
              setShowBeatleapStudio(false);
              setEditVideoUrl(null);
              setActiveGalleryTab('projekte'); // 💾 Direkt zu "Meine Projekte"
            }}
            onNewProject={() => {
              setShowBeatleapStudio(false);
              setEditVideoUrl(null);
            }}
          />
        )}
      </div>
    );
  }

  return null;
};

// ═══════════════════════════════════════════════════════════════
// STYLES (matching Beatleap look)
// ═══════════════════════════════════════════════════════════════
const styles = {
  fullScreen: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: '#000',
    color: 'white',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 9999,
    overscrollBehavior: 'contain',
    touchAction: 'none',
    WebkitOverflowScrolling: 'auto'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '15px 20px',
    borderBottom: '1px solid #333'
  },
  headerTitle: {
    margin: 0,
    fontSize: '18px',
    color: '#10B981'
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: 'white',
    fontSize: '24px',
    cursor: 'pointer',
    padding: '5px 10px'
  },
  doneBtn: {
    background: '#10B981',
    border: 'none',
    borderRadius: '8px',
    color: 'white',
    padding: '8px 16px',
    fontWeight: 'bold',
    cursor: 'pointer'
  },
  tabBar: {
    display: 'flex',
    padding: '8px 16px',
    gap: '8px',
    borderBottom: '1px solid #333'
  },
  tabActive: {
    padding: '6px 14px',
    background: 'rgba(255,255,255,0.1)',
    borderRadius: '16px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    whiteSpace: 'nowrap'
  },
  tabInactive: {
    padding: '6px 14px',
    background: 'transparent',
    borderRadius: '16px',
    fontSize: '13px',
    fontWeight: '500',
    color: 'rgba(255,255,255,0.5)',
    cursor: 'pointer',
    whiteSpace: 'nowrap'
  },
  hint: {
    textAlign: 'center',
    padding: '10px',
    color: '#10B981',
    fontSize: '14px',
    background: 'rgba(16,185,129,0.1)'
  },
  galleryContainer: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '5px',
    WebkitOverflowScrolling: 'touch',
    overscrollBehavior: 'contain',
    touchAction: 'pan-y'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '3px'
  },
  gridItem: {
    aspectRatio: '3/4',
    position: 'relative',
    cursor: 'pointer',
    overflow: 'hidden'
  },
  gridImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  // Green overlay for selected items (like Beatleap)
  selectedOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(16, 185, 129, 0.5)',
    pointerEvents: 'none'
  },
  // Big checkmark in center
  bigCheck: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '50px',
    height: '50px',
    background: '#10B981',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '28px',
    color: 'black',
    fontWeight: 'bold',
    pointerEvents: 'none'
  },
  // Number badge top right
  numberBadge: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    width: '26px',
    height: '26px',
    background: '#10B981',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    color: 'black',
    fontWeight: 'bold',
    pointerEvents: 'none'
  },
  videoBadge: {
    position: 'absolute',
    bottom: '5px',
    right: '5px',
    width: '28px',
    height: '28px',
    background: 'rgba(59,130,246,0.9)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    color: 'white',
    zIndex: 10
  },
  bottomBar: {
    padding: '15px 20px',
    borderTop: '1px solid #333',
    textAlign: 'center',
    position: 'relative',
    zIndex: 100
  },
  loading: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#888'
  },
  empty: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#888',
    lineHeight: '1.8'
  },
  // Config screen - matching ID Card dark elegant style
  container: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: '#0a0a0a',
    color: 'white',
    padding: '20px',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    overscrollBehavior: 'contain',
    touchAction: 'pan-y',
    zIndex: 9999
  },
  title: {
    fontSize: '1.8rem',
    fontWeight: 'bold',
    background: 'linear-gradient(90deg, #10B981, #34D399)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent'
  },
  section: {
    background: 'rgba(16,185,129,0.05)',
    border: '1px solid rgba(16,185,129,0.2)',
    borderRadius: '16px',
    padding: '20px',
    marginBottom: '20px'
  },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: '600',
    marginBottom: '15px'
  },
  previewRow: {
    display: 'flex',
    gap: '8px',
    overflowX: 'auto',
    paddingBottom: '10px'
  },
  previewThumb: {
    width: '60px',
    height: '80px',
    objectFit: 'cover',
    borderRadius: '8px',
    border: '2px solid #10B981'
  },
  moreCount: {
    width: '60px',
    height: '80px',
    background: 'rgba(16,185,129,0.2)',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#10B981',
    fontWeight: 'bold'
  },
  changeBtn: {
    marginTop: '10px',
    padding: '8px 16px',
    background: 'rgba(255,255,255,0.1)',
    border: 'none',
    borderRadius: '8px',
    color: 'white',
    cursor: 'pointer'
  },
  musicGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '10px'
  },
  musicItem: {
    padding: '15px',
    background: 'rgba(255,255,255,0.05)',
    borderRadius: '12px',
    cursor: 'pointer',
    border: '2px solid transparent'
  },
  musicItemSelected: {
    border: '2px solid #10B981',
    background: 'rgba(16,185,129,0.1)'
  },
  durationValue: {
    textAlign: 'center',
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#10B981',
    margin: '10px 0'
  },
  magicButton: {
    width: '100%',
    padding: '18px',
    background: 'linear-gradient(90deg, #10B981, #34D399)',
    border: 'none',
    borderRadius: '16px',
    color: 'white',
    fontSize: '1.2rem',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginTop: '20px'
  },
  backBtn: {
    width: '100%',
    padding: '15px',
    background: 'rgba(255,255,255,0.1)',
    border: 'none',
    borderRadius: '12px',
    color: 'white',
    cursor: 'pointer',
    marginTop: '10px'
  },
  // Processing
  processingBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '70vh',
    textAlign: 'center'
  },
  spinner: {
    width: '60px',
    height: '60px',
    border: '4px solid rgba(255,255,255,0.1)',
    borderTop: '4px solid #10B981',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '20px'
  },
  progressBar: {
    width: '80%',
    maxWidth: '300px',
    height: '8px',
    background: 'rgba(255,255,255,0.1)',
    borderRadius: '4px',
    overflow: 'hidden',
    marginTop: '20px'
  },
  progressFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #10B981, #34D399)',
    transition: 'width 0.3s'
  },
  // Voting
  variantsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '15px',
    marginBottom: '20px'
  },
  variantCard: {
    background: 'rgba(255,255,255,0.05)',
    borderRadius: '16px',
    overflow: 'hidden',
    cursor: 'pointer',
    border: '3px solid transparent'
  },
  variantCardSelected: {
    border: '3px solid #10B981'
  },
  variantVideo: {
    width: '100%',
    aspectRatio: '9/16',
    objectFit: 'cover',
    background: '#000'
  },
  variantInfo: {
    padding: '12px',
    textAlign: 'center'
  },
  noVideo: {
    width: '100%',
    aspectRatio: '9/16',
    background: 'rgba(255,255,255,0.05)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    color: '#666'
  },
  videoContainer: {
    position: 'relative',
    width: '100%'
  },
  timeDisplay: {
    position: 'absolute',
    bottom: '8px',
    right: '8px',
    background: 'rgba(0,0,0,0.6)',
    color: 'white',
    fontSize: '11px',
    padding: '3px 6px',
    borderRadius: '4px',
    fontFamily: 'monospace'
  },
  controlsRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: '12px',
    padding: '10px',
    borderBottom: '1px solid rgba(255,255,255,0.1)'
  },
  controlBtnBelow: {
    border: 'none',
    fontSize: '18px',
    cursor: 'pointer',
    padding: '8px 16px',
    borderRadius: '20px',
    transition: 'background 0.2s'
  },
  metaRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: '10px',
    fontSize: '11px',
    color: '#888',
    marginBottom: '4px'
  },
  downloadBtn: {
    display: 'inline-block',
    marginTop: '8px',
    padding: '6px 12px',
    background: 'rgba(16,185,129,0.2)',
    border: '1px solid #10B981',
    borderRadius: '8px',
    color: '#10B981',
    fontSize: '12px',
    textDecoration: 'none',
    cursor: 'pointer'
  },
  // Project Preview Modal Styles - MINIMALISTISCH & EDEL (Mobile-First)
  previewModal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: '#000',
    zIndex: 10000,
    display: 'flex',
    flexDirection: 'column',
    color: 'white',
    overscrollBehavior: 'contain',
    touchAction: 'none',
    height: '100dvh',
    paddingTop: 'env(safe-area-inset-top)',
    paddingBottom: 'env(safe-area-inset-bottom)'
  },
  previewHeaderMinimal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    background: '#000',
    flexShrink: 0
  },
  previewBackBtnMinimal: {
    background: 'rgba(16,185,129,0.15)',
    border: '1px solid rgba(16,185,129,0.3)',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#10B981',
    cursor: 'pointer',
    backdropFilter: 'blur(10px)'
  },
  previewTitleMinimal: {
    fontSize: '12px',
    color: 'rgba(255,255,255,0.5)',
    fontWeight: '400'
  },
  previewEditBtnMinimal: {
    background: 'rgba(16,185,129,0.15)',
    border: '1px solid rgba(16,185,129,0.3)',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#10B981',
    cursor: 'pointer',
    backdropFilter: 'blur(10px)'
  },
  previewVideoContainerMinimal: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#000',
    position: 'relative',
    cursor: 'pointer',
    minHeight: 0,
    maxHeight: '55vh'
  },
  previewVideoMinimal: {
    width: '100%',
    height: '100%',
    objectFit: 'contain'
  },
  previewPlayOverlayMinimal: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0,0,0,0.2)'
  },
  previewPlayBtnMinimal: {
    width: '70px',
    height: '70px',
    borderRadius: '50%',
    background: 'rgba(16,185,129,0.8)',
    backdropFilter: 'blur(10px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px solid rgba(16,185,129,0.5)'
  },
  previewProgressMinimal: {
    padding: '12px 16px 8px',
    background: '#000',
    flexShrink: 0
  },
  sliderTrack: {
    position: 'relative',
    width: '100%',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    touchAction: 'none',
    marginBottom: '4px',
    background: 'transparent'
  },
  sliderBg: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    height: '4px',
    background: 'rgba(255,255,255,0.2)',
    borderRadius: '2px',
    pointerEvents: 'none'
  },
  sliderFill: {
    position: 'absolute',
    left: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    height: '4px',
    background: '#10B981',
    borderRadius: '2px',
    pointerEvents: 'none'
  },
  sliderThumb: {
    position: 'absolute',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    width: '16px',
    height: '16px',
    background: '#fff',
    borderRadius: '50%',
    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
    pointerEvents: 'none'
  },
  previewTimeRowMinimal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: '11px',
    color: 'rgba(255,255,255,0.5)',
    fontFamily: 'monospace'
  },
  previewControlsMinimal: {
    display: 'flex',
    gap: '12px'
  },
  previewIconBtn: {
    background: 'none',
    border: 'none',
    fontSize: '18px',
    cursor: 'pointer',
    padding: '2px',
    opacity: 0.8
  },
  previewActionsMinimal: {
    display: 'flex',
    gap: '10px',
    padding: '12px 16px',
    paddingBottom: '20px',
    background: '#000',
    flexShrink: 0
  },
  previewMainBtn: {
    flex: 1,
    padding: '12px 16px',
    background: 'rgba(16,185,129,0.2)',
    border: '1px solid rgba(16,185,129,0.4)',
    borderRadius: '12px',
    color: '#10B981',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    backdropFilter: 'blur(10px)'
  },
  previewSecBtn: {
    width: '46px',
    height: '46px',
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    cursor: 'pointer',
    textDecoration: 'none'
  },
  previewDelBtn: {
    width: '46px',
    height: '46px',
    background: 'rgba(239,68,68,0.1)',
    border: '1px solid rgba(239,68,68,0.3)',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#EF4444',
    cursor: 'pointer'
  },
  // Delete Confirmation Dialog - iOS Style Minimalistisch
  deleteOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 20000,
    padding: '20px'
  },
  deleteDialog: {
    background: '#0a0a0a',
    borderRadius: '14px',
    padding: '20px 24px',
    textAlign: 'left',
    maxWidth: '300px',
    width: '100%',
    border: '1px solid rgba(16,185,129,0.2)'
  },
  deleteIcon: {
    display: 'none'
  },
  deleteTitle: {
    margin: '0 0 12px 0',
    fontSize: '17px',
    fontWeight: '600',
    color: 'white',
    lineHeight: '1.4'
  },
  deleteText: {
    margin: '0 0 20px 0',
    fontSize: '14px',
    color: 'rgba(255,255,255,0.6)',
    lineHeight: '1.5'
  },
  deleteButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '20px'
  },
  deleteCancelBtn: {
    padding: '8px 4px',
    background: 'transparent',
    border: 'none',
    color: '#10B981',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  deleteConfirmBtn: {
    padding: '8px 4px',
    background: 'transparent',
    border: 'none',
    color: '#10B981',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer'
  }
};

// Spinner CSS
if (typeof document !== 'undefined' && !document.querySelector('#magic-spinner')) {
  const style = document.createElement('style');
  style.id = 'magic-spinner';
  style.textContent = '@keyframes spin{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}';
  document.head.appendChild(style);
}

export default MagicCreate;
